import React from "react";
import { Row, Col } from "reactstrap";
import { useWizard } from "react-use-wizard";
import { useFormContext } from "react-hook-form";
import s from "./styles.scss";
import withStyles from "isomorphic-style-loader/withStyles";
import PrimaryButton from "../primaryButton";
import { ObjectSchema } from "yup";

interface WizardFooterProps {
  onFinish?: (data: any) => void | Promise<void>;
  onNext?: (data: any) => Promise<void> | void;
  schema: ObjectSchema<any>;
  loading?: boolean;
  loadingText?: string;
  loadingAnimation?: "pulse" | "shimmer" | "none";
  disabled?: boolean;
  nextButtonText?: string;
  finishButtonText?: string;
  backButtonText?: string;
  customNextStep?: number;
  customBackStep?: number;
  hideNextButton?: boolean;
  hideBackButton?: boolean;
  validationFields?: string[];
}

const WizardFooter: React.FC<WizardFooterProps> = ({
  onFinish,
  onNext,
  schema,
  loading = false,
  loadingText,
  loadingAnimation,
  disabled = false,
  nextButtonText,
  finishButtonText,
  backButtonText,
  customNextStep,
  customBackStep,
  hideNextButton = false,
  hideBackButton = false,
  validationFields,
}) => {
  const { nextStep, previousStep, activeStep, isLastStep, goToStep } = useWizard();
  const { trigger, getValues } = useFormContext();

  const fieldsToValidate = validationFields?.length
    ? validationFields
    : schema?.fields
      ? Object.keys(schema.fields)
      : [];

  const handleNext = async () => {
    if (disabled || loading) return;

    const valid = fieldsToValidate.length > 0
      ? await trigger(fieldsToValidate)
      : true;

    if (valid) {
      const allData = getValues();

      if (isLastStep && onFinish) {
        await onFinish(allData);
      } else if (!isLastStep) {
        if (onNext) {
          await onNext(allData);
        }

        if (customNextStep !== undefined) {
          await goToStep(customNextStep);
        } else {
          await nextStep();
        }
      }
    }
  };

  const handleBack = () => {
    if (disabled || loading) return;
    
    if (customBackStep !== undefined) {
      goToStep(customBackStep);
    } else {
      previousStep();
    }
  };

  return (
    <div className={s.stepFooterContainer}>
      <Row className="justify-content-center">
        <Col md={7}>
          <hr className="border-secondary" />
        </Col>
      </Row>
      <Row className="justify-content-center mt-3 mb-3">
        {hideBackButton ? (
          <Col md={8} className="d-flex justify-content-center">
            {!hideNextButton && (
              <PrimaryButton
                onClick={handleNext}
                loading={loading}
                loadingText={loadingText}
                loadingAnimation={loadingAnimation}
                disabled={disabled}
              >
                {isLastStep
                  ? finishButtonText || "Finish"
                  : nextButtonText || "Next"}
              </PrimaryButton>
            )}
          </Col>
        ) : (
          <>
            <Col md={4} className="d-flex justify-content-start">
              <PrimaryButton
                onClick={handleBack}
                className="ms-3"
                disabled={activeStep === 0 || disabled || loading}
              >
                {backButtonText || "Back"}
              </PrimaryButton>
            </Col>
            <Col md={4} className="d-flex justify-content-end">
              {!hideNextButton && (
                <PrimaryButton
                  onClick={handleNext}
                  className="me-3"
                  loading={loading}
                  loadingText={loadingText}
                  loadingAnimation={loadingAnimation}
                  disabled={disabled}
                >
                  {isLastStep
                    ? finishButtonText || "Finish"
                    : nextButtonText || "Next"}
                </PrimaryButton>
              )}
            </Col>
          </>
        )}
      </Row>
    </div>
  );
};

export default withStyles(s)(WizardFooter);
